import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './index.module.scss';

type RemovePrefix<T extends string, Prefix extends T> = T extends `${Prefix}${string}` ? never : T;

/**
 * All error codes that can be passed to ErrorMessage.
 * Nested keys are removed since they will result in a non-string return value. They
 * can be processed manually.
 */
type ErrorCode = RemovePrefix<TFuncKey<'translation', 'error'>, 'password_rejected'>;

export type ErrorType = ErrorCode | { code: ErrorCode; data?: Record<string, unknown> };

export type Props = {
  readonly error?: ErrorType;
  readonly className?: string;
  readonly id?: string;
  readonly children?: ReactNode;
};

/**
 * Lumenia 托管认证界面：错误是**旁注，不是填充块**。
 *
 * 落成形态：单色警示图标 + 砖红文字，没有边框、圆角、底色与内边距，靠所在表单的
 * gap 与上下文分开。砖红只出现在文字与图标上，画面里没有任何红色线条或色块。
 * 试过并否掉的两条：顶部一条中性细线（读成分组分隔符，把表单切成两半）、
 * 左侧一条砖红竖线（竖左 bar 强调色在反 slop 清单里）。
 *
 * 非颜色标记由三样承担，一样都不靠色觉：形状（图标）、内容（错误文字）、
 * 布局变化（整条出现）。role="alert" 原样保留。
 * 新增可选 id：供验证码六格的 aria-describedby 指过来。
 */
const ErrorMessage = ({ error, className, id, children }: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'error' });

  const getMessage = () => {
    if (!error) {
      return children;
    }

    if (typeof error === 'string') {
      return t(error);
    }

    return t(error.code, { ...error.data });
  };

  return (
    <div id={id} role="alert" className={classNames(styles.error, className)}>
      <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 9v5" />
        <path d="M12 17.5v.01" />
        <path d="M10.3 3.9 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <span className={styles.text}>{getMessage()}</span>
    </div>
  );
};

export default ErrorMessage;
